import { isEqual, uniqWith } from 'lodash';
import { v4 as uuid } from 'uuid';
import { uploadBase64ToS3 } from '../advisorHub/clientSideServices/aws';
import { getLibrarySceneById } from '../advisorHub/clientSideServices/library';
// import { getRenderingStore } from '../advisorHub/components/Polotno/polotnoInit';
import { StoreType } from 'polotno/model/store';
import { ILibraryScene } from '../interfaces';
import { IPolotnoJSON } from '../mappers/polotno';

type PolotnoLayer = {
  json: IPolotnoJSON;
  exportOptions?: object;
  uploadOptions?: {
    bucket: string;
    Key: string;
    ContentType: string;
  };
};

// Timeout wrapper for promises
const withTimeout = <T>(promise: Promise<T>, timeoutMs: number, operation: string): Promise<T> => {
  return Promise.race([
    promise,
    new Promise<T>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`${operation} timed out after ${timeoutMs}ms`));
      }, timeoutMs);
    })
  ]);
};

// Resource cleanup helper
const cleanupRenderingStore = (renderingStore: StoreType, pageIds: string[]) => {
  try {
    if (renderingStore && pageIds.length > 0) {
      renderingStore.deletePages(pageIds);
    }
  } catch (error) {
    console.warn('Failed to cleanup rendering store pages:', error);
  }
};

export const generatedImageFromServer = async (layers: PolotnoLayer[]) => {
  let renderingStore = window['polotnoRenderingStore'] as StoreType;

  if (!renderingStore) {
    console.error('Polotno rendering store not found');
    // Try to initialize it with timeout
    try {
      const { getRenderingStore } = await withTimeout(
        import('../advisorHub/components/Polotno/polotnoInit'),
        5000,
        'Polotno store initialization'
      );
      renderingStore = getRenderingStore();
      if (!renderingStore) {
        throw new Error('Failed to initialize rendering store');
      }
    } catch (error) {
      console.error('Failed to initialize rendering store in generatedImageFromServer:', error);
      throw new Error('Polotno rendering store not initialized');
    }
  }

  // Check if rendering store has too many pages (memory leak protection)
  if (renderingStore.pages && renderingStore.pages.length > 50) {
    console.warn(`Rendering store has ${renderingStore.pages.length} pages, resetting to prevent memory issues`);
    try {
      const { resetRenderingStore } = await import('../advisorHub/components/Polotno/polotnoInit');
      renderingStore = resetRenderingStore();
    } catch (error) {
      console.error('Failed to reset rendering store:', error);
    }
  }

  // Track created pages for cleanup
  const createdPageIds: string[] = [];
  
  try {
    const urls = await Promise.all(
      layers.map(async (layer, index) => {
        let pageId: string | null = null;
        
        try {
          if (!layer) {
            console.warn(`Layer ${index} is null or undefined`);
            return null;
          }
          
          const { json, exportOptions, uploadOptions } = layer;
          if (!json || !json.pages || json.pages.length === 0) {
            console.warn(`No pages found in layer ${index} JSON:`, { json });
            return null;
          }
          
          const pageJson = json.pages[0];
          pageId = uuid();
          
          // Add page with timeout protection
          const page = await withTimeout(
            Promise.resolve(renderingStore.addPage({
              ...pageJson,
              id: pageId
            })),
            10000,
            `Adding page ${index}`
          );
          
          createdPageIds.push(pageId);

          const { isAnimated, ...options } = exportOptions || ({} as any);

          if (isAnimated) {
            const actualEl = json.pages[0].children[0];
            page.setSize({
              width: actualEl.width,
              height: actualEl.height,
            });
            page.children[0].set({
              x: 0,
              y: 0,
              rotation: 0
            });
          }

          // Generate image with timeout protection and stage validation
          let dataUrl: string;
          try {
            // Wait a moment for the stage to be ready
            await new Promise(resolve => setTimeout(resolve, 100));
            
            dataUrl = await withTimeout(
              renderingStore.toDataURL({
                pageId: page.id,
                ...options
              }) as Promise<string>,
              30000, // 30 second timeout for image generation
              `Generating image for layer ${index}`
            );
          } catch (stageError) {
            // If stage error occurs, try once more after a longer delay
            if (stageError.message?.includes('No stage is found')) {
              console.warn(`Stage not found for layer ${index}, retrying after delay...`);
              await new Promise(resolve => setTimeout(resolve, 500));
              
              dataUrl = await withTimeout(
                renderingStore.toDataURL({
                  pageId: page.id,
                  ...options
                }) as Promise<string>,
                30000,
                `Retrying image generation for layer ${index}`
              );
            } else {
              throw stageError;
            }
          }
          
          // Clean up page immediately after use
          renderingStore.deletePages([page.id]);
          const pageIndex = createdPageIds.indexOf(pageId);
          if (pageIndex > -1) {
            createdPageIds.splice(pageIndex, 1);
          }
          
          if (layer.uploadOptions) {
            // Upload with timeout protection
            const uploadResult = await withTimeout(
              uploadBase64ToS3({
                Bucket: uploadOptions.bucket,
                Key: uploadOptions.Key,
                ContentType: uploadOptions.ContentType,
                Body: dataUrl
              }),
              60000, // 60 second timeout for upload
              `Uploading image for layer ${index}`
            );
            
            return uploadResult;
          }
          
          return dataUrl;
        } catch (error) {
          const isStageError = error.message?.includes('No stage is found');
          console.error(`Error generating image for layer ${index}:`, {
            error: error.message,
            isStageError,
            pageId,
            layerInfo: {
              hasJson: !!layer?.json,
              hasPages: !!layer?.json?.pages?.length,
              pageId: layer?.json?.pages?.[0]?.id
            }
          });
          
          // Clean up page if it was created
          if (pageId) {
            try {
              renderingStore.deletePages([pageId]);
              const pageIndex = createdPageIds.indexOf(pageId);
              if (pageIndex > -1) {
                createdPageIds.splice(pageIndex, 1);
              }
            } catch (cleanupError) {
              console.warn(`Failed to cleanup page ${pageId}:`, cleanupError);
            }
          }
          
          // For stage errors, return null to continue with other layers
          // For other errors, we might want to throw to stop the process
          if (isStageError) {
            console.warn(`Stage error for layer ${index}, continuing with remaining layers...`);
            return null;
          }
          
          return null;
        }
      })
    );
    
    return urls as string[];
  } catch (error) {
    console.error('Critical error in generatedImageFromServer:', error);
    
    // Emergency cleanup of any remaining pages
    cleanupRenderingStore(renderingStore, createdPageIds);
    
    throw error;
  } finally {
    // Final cleanup of any remaining pages
    cleanupRenderingStore(renderingStore, createdPageIds);
  }
};



export const scenePageHasChanged = async (sceneId: string, pages) => {
  const originalScene: ILibraryScene = await getLibrarySceneById(sceneId);
  const originalPages = JSON.parse(originalScene.content.overlay)?.pages || [];
  let scenePageHasChanged = false;
  if (!pages || !pages.length) scenePageHasChanged = true;
  pages.map((page) => {
    const originalPage = originalPages.find((p) => p.id === page.id);
    if (
      !originalPage ||
      !isEqual(
        JSON.parse(JSON.stringify(originalPage)),
        JSON.parse(JSON.stringify(page))
      )
    ) {
      scenePageHasChanged = true;
      return scenePageHasChanged;
    }
  });

  return scenePageHasChanged;
};

export const removeUnusedFontsInScene = (scene: ILibraryScene) => {
  try {
    if (!scene.content.overlay || !scene.content.settings) return scene;
    const overlay = JSON.parse(scene.content.overlay);
    const customFonts = JSON.parse(scene.content.settings)?.customFont;
    const usedFonts = [];

    overlay.pages.forEach((page) => {
      page.children.forEach((child) => {
        if (child.fontFamily) {
          usedFonts.push(child.fontFamily);
        }
      });
    });

    const filteredFontsList = customFonts.filter((font) =>
      usedFonts.includes(font.fontFamily)
    );

    return {
      ...scene,
      content: {
        ...scene.content,
        settings: JSON.stringify({
          ...JSON.parse(scene.content.settings),
          customFont: uniqWith(filteredFontsList, isEqual)
        })
      }
    };
  } catch (error) {
    return scene;
  }
};

export const removeUnusedFontsInPage = (page: IPolotnoJSON) => {
  try {
    if (!page.fonts || !page.pages) return page;
    const usedFonts = [];

    page.pages.forEach((page) => {
      page.children.forEach((child) => {
        if (child.fontFamily) {
          usedFonts.push(child.fontFamily);
        }
      });
    });

    const filteredFontsList = page.fonts.filter((font) =>
      usedFonts.includes(font.fontFamily)
    );

    return {
      ...page,
      fonts: uniqWith(filteredFontsList, isEqual)
    };
  } catch (error) {
    return page;
  }
};
type RGBColor = {
  red: string;
  green: string;
  blue: string;
  alpha: string;
};

export function rgbToObj (rgb: string): {
  red: string;
  green: string;
  blue: string;
  alpha?: string;
} {
  if (rgb === 'black')
    return {
      red: '0',
      green: '0',
      blue: '0'
    };
  if (rgb === 'white')
    return {
      red: '255',
      green: '255',
      blue: '255'
    };
  const colors = ['red', 'green', 'blue', 'alpha'];

  const colorArr = rgb
    .slice(rgb.indexOf('(') + 1, rgb.indexOf(')'))
    .split(',');

  const obj: RGBColor = {} as any;

  colorArr.forEach((k, i) => {
    obj[colors[i]] = k;
  });

  return obj;
}

export const withOpacity = (_color: string, opacity: number) => {
  const color = rgbToObj(_color);
  const { red, green, blue } = color;
  return `rgba(${red}, ${green}, ${blue}, ${opacity})`;
};

export const getBlur = (setting) => {
  if (setting.blurEnabled) {
    return `${setting.blurRadius / 2}px`;
  }
  return '0';
};

export const getShadow = (setting) =>{
  if (setting.shadowEnabled)
    return `${setting.shadowOffsetX}px ${setting.shadowOffsetY}px ${
      setting.shadowBlur
    }px ${withOpacity(setting.shadowColor, setting.shadowOpacity)}`;
  return '';
}

export const getTextShadow = (setting) => {
  if (setting.shadowEnabled)
    return `${setting.shadowOffsetX}px ${setting.shadowOffsetY}px ${
      setting.shadowBlur
    }px ${withOpacity(setting.shadowColor, setting.shadowOpacity)}`;
  return 'none';
};

export const getBackgroundColor = (setting) => {
  if(setting.backgroundEnabled) {
    return
  }
  return 'unset';
}

export const getBoxShadow = (setting) => {
  if (setting.backgroundEnabled) {
    const color = withOpacity(setting.backgroundColor, setting.backgroundOpacity);
    return '0px 0px 0px 0px ' + color;
  }
}